import { createRef, PureComponent, RefObject } from 'react';
import { connect, MapDispatchToProps } from 'react-redux';

import messages from './Messages';
import Button from 'components/Button';
import { display } from 'components/ErrorView/ErrorDialog';
import notify from 'components/Notify';
import APopover from '../../../../APopover';
import { smartDateString } from '../../../../../utils/date';
import { VersionWrapper } from './styles';
import { Chip } from '../../../../Chips/stylesAuto';
import Input from '../../../../Input/Input';
import { HintIcon, HintText, QuestionHintBox } from './GeneralQuestion/styles';
import { appDispatch } from 'store/store';
import {
  ISurveyItem,
  ISurveyItemWithData,
  ISurveyVersionInfo,
  ISurveyView,
} from 'store/survey/surveyTypes';
import {
  createSurveyVersion,
  createSurveyVersionFn,
  deleteSurveyVersion,
  setSurveyVersion,
  setSurveyVersionFn,
} from 'store/survey/surveySlice';
import i18n, { languageDirection } from '../../../../../i18n';
import LocalIcon from 'ui/LocalIcon';
import { red } from 'utils/style/colors';

interface IStateToProps {
  surveyItemTags: Array<{ label: string; value: number }>;
}

// TODO: define types with "typeof" for dispatches
interface IDispatchToProps {
  createNewVersion: (
    versionInfo: ISurveyVersionInfo,
    providerSurveyItemData: ISurveyItem
  ) => ReturnType<typeof createSurveyVersionFn>;
  setVersion: (
    versionId: string,
    providerSurveyItemData: ISurveyItem
  ) => ReturnType<typeof setSurveyVersionFn>;
}
interface IOwnProps {
  item: ISurveyItemWithData;
  survey: ISurveyView;
}
type IComponentProps = IOwnProps & IStateToProps & IDispatchToProps;

interface IComponentStates {
  saving: boolean;
  newItemTitle?: string;
  newItem?: string;
  adding: boolean;
}

class SurveyVersions extends PureComponent<IComponentProps, IComponentStates> {
  state = {
    saving: false,
    adding: false,
  };

  newVersionTitleRef: RefObject<HTMLInputElement> = createRef();
  newVersionDescriptionRef: RefObject<HTMLInputElement> = createRef();

  handleAddVersion = () => {
    this.setState((prevState) => ({
      ...prevState,
      adding: !this.state.adding,
    }));
  };

  handleCreateVersion = () => {
    const versionInfo: ISurveyVersionInfo = {
      versionTitle: this.newVersionTitleRef!.current!.value,
      versionDescription: this.newVersionDescriptionRef!.current!.value,
    };
    return this.props
      .createNewVersion(versionInfo, this.props.item.data)
      .then(() => {
        notify(
          messages.versionCreatedSuccessfully(versionInfo.versionTitle || '')
        );
        this.setState({ adding: false, saving: false });
        return Promise.resolve();
      })
      .catch((err: any) => {
        display(err);
        this.setState({ saving: false });
        return Promise.reject();
      });
  };

  private makeActive(versionInfo: ISurveyVersionInfo) {
    return this.props
      .setVersion(versionInfo.versionId!, this.props.item.data)
      .then(() => {
        notify(
          messages.versionCreatedSuccessfully(versionInfo.versionTitle || '')
        );
        this.setState({ adding: false, saving: false });
        return Promise.resolve();
      })
      .catch((err: any) => {
        display(err);
        this.setState({ saving: false });
        return Promise.reject();
      });
  }

  handleDeleteSurveyVersion = async (versionId: string) => {
    const result = await appDispatch(
      deleteSurveyVersion({
        versionId: versionId,
        providerSurveyItemData: this.props.item.data,
      })
    );
    if (result.status === 'Failed') {
      const responseIdList = result.message;
      notify(
        `${i18n.t('canNotBedeletedBecauseOfResponses', { ns: 'survey' })} \n ${responseIdList.join(',')}`,
        'error',
        false
      );
    }
  };

  render() {
    const { item, survey } = this.props;
    const { saving, adding } = this.state;

    return (
      <div className="modal-content flex-box" style={{ paddingBottom: '0' }}>
        <div>
          {item.data.editToken && (
            <APopover
              arrow
              curveBorder
              placement="bottom-start"
              trigger="click"
              show={adding}
              target={
                <Button
                  color="green"
                  onClick={this.handleAddVersion}
                  style={{
                    float: `${languageDirection() === 'rtl' ? 'left' : 'right'}`,
                    marginBottom: '2rem',
                    clear: 'both',
                  }}
                >
                  {i18n.t('createNewVersion', { ns: 'survey' }) as string}
                </Button>
              }
            >
              <div
                style={{
                  width: '100vw',
                }}
              >
                <p style={{ width: '100%' }}>
                  <QuestionHintBox>
                    <HintIcon type="info" />
                    <HintText style={{ whiteSpace: 'pre-wrap' }}>
                      <h5>
                        {
                          i18n.t('newVersionInfoTitle', {
                            ns: 'survey',
                          }) as string
                        }
                      </h5>
                      <span>
                        <p>
                          {i18n.t('newVersionInfo', { ns: 'survey' }) as string}
                        </p>
                      </span>
                    </HintText>
                  </QuestionHintBox>
                </p>
                <Input
                  name="title"
                  id="title"
                  type="text"
                  label={i18n.t('versionTitle', { ns: 'survey' }) as string}
                  forwardRef={this.newVersionTitleRef}
                />
                <span
                  className="lever"
                  style={{ marginInlineStart: '0', marginInlineEnd: '10px' }}
                />
                <Input
                  name="description"
                  id="description"
                  type="text"
                  label={
                    i18n.t('versionDescription', { ns: 'survey' }) as string
                  }
                  forwardRef={this.newVersionDescriptionRef}
                />
                <span
                  className="lever"
                  style={{ marginInlineStart: '0', marginInlineEnd: '10px' }}
                />
                <Button
                  onClick={this.handleCreateVersion}
                  loading={saving}
                  color="green"
                >
                  {i18n.t('create', { ns: 'survey' }) as string}
                </Button>
              </div>
            </APopover>
          )}
        </div>
        <div>
          {survey.versions ? (
            <div className="flex flex-col items-center w-full">
              {Object.values(survey.versions)
                .sort(
                  (a, b) =>
                    (b.versionInfo!.creationDate || 0) -
                    (a.versionInfo!.creationDate || 0)
                )
                .map((version) => (
                  <VersionWrapper key={version.versionInfo!.versionId}>
                    <p>
                      {i18n.t('versionTitle', { ns: 'survey' }) as string} :{' '}
                      <b>{version.versionInfo!.versionTitle}</b>
                    </p>
                    <p>
                      {i18n.t('versionDescription', { ns: 'survey' }) as string}{' '}
                      : <b>{version.versionInfo!.versionDescription}</b>
                    </p>
                    <p>
                      {i18n.t('creationDate', { ns: 'survey' }) as string}:{' '}
                      <b>
                        {smartDateString(
                          new Date(
                            version.versionInfo!.creationDate ||
                              item.data.creationDate
                          ),
                          true
                        )}
                      </b>
                    </p>
                    {version.versionInfo!.versionId !==
                    survey.currentVersion ? (
                      <div className="flex justify-between">
                        <Button
                          onClick={() => this.makeActive(version.versionInfo!)}
                        >
                          {i18n.t('makeActive', { ns: 'survey' }) as string}
                        </Button>

                        <LocalIcon
                          onClick={() => {
                            if (version.versionInfo?.versionId)
                              this.handleDeleteSurveyVersion(
                                version.versionInfo?.versionId
                              );
                          }}
                          isLink={true}
                          iconName="trash.svg"
                          color={red}
                        />
                      </div>
                    ) : (
                      <p>
                        <Chip selected={true}>
                          {i18n.t('activeVersion', { ns: 'survey' }) as string}
                        </Chip>
                      </p>
                    )}
                  </VersionWrapper>
                ))}
            </div>
          ) : (
            <p>no match found </p>
          )}
        </div>
      </div>
    );
  }
}

// TODO: We must use bindActionCreators here
const mapDispatchToProps: MapDispatchToProps<
  IDispatchToProps,
  IOwnProps
> = () => {
  return {
    createNewVersion: (
      versionInfo: ISurveyVersionInfo,
      providerSurveyItemData: ISurveyItem
    ) =>
      appDispatch(createSurveyVersion({ versionInfo, providerSurveyItemData })),
    setVersion: (versionId: string, providerSurveyItemData: ISurveyItem) =>
      appDispatch(setSurveyVersion({ versionId, providerSurveyItemData })),
  };
};

export default connect(null, mapDispatchToProps)(SurveyVersions);
